export const PAGE_SIZE = 7
export const FETCH_PAGE_SIZE = 50

export function createListenerScope() {
  const controller = new AbortController()

  return {
    signal: controller.signal,
    listen(target, type, listener, options = {}) {
      target?.addEventListener?.(type, listener, { ...options, signal: controller.signal })
    },
    abort() {
      controller.abort()
    },
  }
}

export function createRequestGate() {
  let generation = 0
  let controller

  return {
    begin() {
      controller?.abort()
      controller = new AbortController()
      const currentGeneration = ++generation
      const currentController = controller

      return {
        signal: currentController.signal,
        isCurrent: () => currentGeneration === generation && !currentController.signal.aborted,
      }
    },
    abort() {
      controller?.abort()
      generation += 1
    },
  }
}

function mergeArticles(groups) {
  const articlesByUrl = new Map()

  for (const articles of groups) {
    for (const article of articles) {
      if (article?.url && !articlesByUrl.has(article.url)) {
        articlesByUrl.set(article.url, article)
      }
    }
  }

  return [...articlesByUrl.values()].sort((left, right) => {
    const dateDifference = Number(right.updatedAt || 0) - Number(left.updatedAt || 0)
    return dateDifference || String(left.url).localeCompare(String(right.url))
  })
}

async function fetchFeedPage(handle, page, { fetchImpl, signal }) {
  const response = await fetchImpl(
    `/blogs/${encodeURIComponent(handle)}?view=articles-json&page=${page}`,
    { signal },
  )
  if (!response.ok) throw new Error(`Blog feed unavailable: ${handle}, page ${page}`)
  return response.json()
}

export async function loadCurrentBlogArticles(
  handle,
  { fetchImpl = globalThis.fetch, signal } = {},
) {
  try {
    const firstPage = await fetchFeedPage(handle, 1, { fetchImpl, signal })
    const pageCount = Math.max(1, Math.ceil(Number(firstPage.count || 0) / FETCH_PAGE_SIZE))
    const remaining = await Promise.allSettled(
      Array.from({ length: pageCount - 1 }, (_, index) =>
        fetchFeedPage(handle, index + 2, { fetchImpl, signal }),
      ),
    )
    const groups = [firstPage.articles || []]
    let failed = false

    for (const result of remaining) {
      if (result.status === 'fulfilled') groups.push(result.value.articles || [])
      else if (result.reason?.name === 'AbortError') throw result.reason
      else failed = true
    }

    return { articles: mergeArticles(groups), failed }
  } catch (error) {
    if (error?.name === 'AbortError') throw error
    return { articles: [], failed: true }
  }
}

export function normalizePage(value, totalPages) {
  const parsed = Number.parseInt(value, 10)
  const safeTotal = Math.max(1, Number(totalPages) || 1)
  if (!Number.isFinite(parsed)) return 1
  return Math.min(Math.max(parsed, 1), safeTotal)
}

export function getPageItems(articles, page) {
  const start = (Math.max(1, Number(page) || 1) - 1) * PAGE_SIZE
  return articles.slice(start, start + PAGE_SIZE)
}

export function pageFromUrl(url, totalPages) {
  return normalizePage(
    new URL(url, globalThis.window?.location).searchParams.get('page'),
    totalPages,
  )
}

export function urlForPage(url, page) {
  const nextUrl = new URL(url, globalThis.window?.location)
  if (Number(page) <= 1) nextUrl.searchParams.delete('page')
  else nextUrl.searchParams.set('page', String(page))
  return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
}

export function scrollBehavior(
  media = globalThis.window?.matchMedia?.('(prefers-reduced-motion: reduce)'),
) {
  return media?.matches ? 'auto' : 'smooth'
}

export function statusForResult(
  { articles, failed },
  emptyLabel = 'No blog posts available.',
  errorLabel = 'Blog posts are temporarily unavailable.',
  partialLabel = 'Some blog posts could not be loaded.',
) {
  if (!articles.length) return failed ? errorLabel : emptyLabel
  return failed ? partialLabel : ''
}

function element(name, className, text) {
  const node = globalThis.document.createElement(name)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

function articleCard(article, placeholderTemplate) {
  const card = element('article', 'blog-default-latest__item')
  const mediaLink = element('a', 'blog-default-latest__image-wrap')
  mediaLink.href = article.url
  mediaLink.setAttribute('aria-label', article.title || 'Blog post')

  if (article.image) {
    const image = element('img', 'blog-default-latest__image')
    image.src = article.image
    image.alt = article.title || ''
    image.loading = 'lazy'
    mediaLink.append(image)
  } else if (placeholderTemplate?.content?.cloneNode) {
    mediaLink.append(placeholderTemplate.content.cloneNode(true))
  }

  const content = element('div', 'blog-default-latest__content')
  const copy = element('div', 'blog-default-latest__copy')
  const title = element('h3', 'blog-default-latest__title')
  const titleLink = element('a', '', article.title || '')
  titleLink.href = article.url
  title.append(titleLink)
  copy.append(title)
  if (article.excerpt) copy.append(element('p', 'blog-default-latest__excerpt', article.excerpt))

  const metadata = element('div', 'blog-default-latest__meta')
  const blogLink = element('a', 'blog-default-latest__category', article.blogTitle || '')
  blogLink.href = article.blogUrl || `/blogs/${article.blogHandle || ''}`
  const date = element('time', 'blog-default-latest__date', article.dateText || '')
  const timestamp = Number(article.updatedAt)
  if (Number.isFinite(timestamp)) date.dateTime = new Date(timestamp * 1000).toISOString()
  metadata.append(blogLink, date)
  content.append(copy, metadata)
  card.append(mediaLink, content)
  return card
}

export function paginationWindow(currentPage, totalPages) {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, index) => index + 1)

  let candidates
  if (currentPage <= 3) {
    candidates = [1, 2, 3, 4, 5, totalPages]
  } else if (currentPage >= totalPages - 2) {
    candidates = [1, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  } else {
    candidates = [1, currentPage - 1, currentPage, currentPage + 1, totalPages]
  }

  const pages = [...new Set(candidates)]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b)
  const items = []
  for (const page of pages) {
    const previousPage = items.at(-1)
    if (typeof previousPage === 'number' && page - previousPage > 1) {
      items.push(page < currentPage ? 'ellipsis-start' : 'ellipsis-end')
    }
    items.push(page)
  }
  return items
}

export function paginationGapTarget(items, index, currentPage) {
  const item = items[index]
  const previousPage = items[index - 1]
  const nextPage = items[index + 1]
  if (
    !String(item).startsWith('ellipsis') ||
    typeof previousPage !== 'number' ||
    typeof nextPage !== 'number'
  ) {
    return null
  }
  const preferredPage = item === 'ellipsis-start' ? currentPage - 2 : currentPage + 2
  return Math.min(nextPage - 1, Math.max(previousPage + 1, preferredPage))
}

const ElementBase = globalThis.HTMLElement || class {}

export class BlogDefaultNavigation extends ElementBase {
  connectedCallback() {
    this.disconnectedCallback()
    this.listenerScope = createListenerScope()
    this.trigger = this.querySelector('[data-blog-nav-trigger]')
    this.dropdown = this.querySelector('[data-blog-nav-dropdown]')
    if (!this.trigger || !this.dropdown) return

    this.close()
    this.listenerScope.listen(this.trigger, 'click', () => {
      if (this.trigger.getAttribute('aria-expanded') === 'true') this.close()
      else this.open()
    })
    this.listenerScope.listen(globalThis.document, 'keydown', (event) => {
      if (event.key === 'Escape' && this.trigger.getAttribute('aria-expanded') === 'true') {
        this.close({ restoreFocus: true })
      }
    })
    this.listenerScope.listen(globalThis.document, 'click', (event) => {
      if (!this.contains(event.target)) this.close()
    })
    this.listenerScope.listen(globalThis.window, 'resize', () => {
      if (globalThis.window.innerWidth > 1024) this.close()
    })
  }

  disconnectedCallback() {
    this.listenerScope?.abort()
    this.listenerScope = undefined
    this.close()
  }

  open() {
    if (!this.trigger || !this.dropdown) return
    this.trigger.setAttribute('aria-expanded', 'true')
    this.dropdown.hidden = false
    this.setAttribute?.('data-open', '')
  }

  close({ restoreFocus = false } = {}) {
    if (!this.trigger || !this.dropdown) return
    const wasOpen = this.trigger.getAttribute('aria-expanded') === 'true'
    this.trigger.setAttribute('aria-expanded', 'false')
    this.dropdown.hidden = true
    this.removeAttribute?.('data-open')
    if (restoreFocus && wasOpen) this.trigger.focus()
  }
}

export class BlogDefaultLatest extends ElementBase {
  connectedCallback() {
    this.disconnectedCallback()
    this.listenerScope = createListenerScope()
    this.requestGate = createRequestGate()
    this.listElement = this.querySelector('[data-blog-list]')
    this.paginationElement = this.querySelector('[data-blog-pagination]')
    this.statusElement = this.querySelector('[data-blog-status]')
    this.placeholderTemplate = this.querySelector('[data-blog-placeholder]')
    this.listenerScope.listen(this.paginationElement, 'click', (event) => {
      const button = event.target.closest?.('[data-blog-page]')
      if (!button || !this.paginationElement.contains(button) || button.disabled) return
      this.showPage(button.dataset.blogPage)
    })
    this.listenerScope.listen(this.paginationElement, 'submit', (event) => {
      const form = event.target.closest?.('[data-blog-goto]')
      if (!form || !this.paginationElement.contains(form)) return
      event.preventDefault()
      const input = form.querySelector('[data-blog-focus="goto"]')
      if (input) this.showPage(input.value)
    })
    this.listenerScope.listen(globalThis.window, 'popstate', () => {
      this.showPage(pageFromUrl(globalThis.window.location.href, this.totalPages), {
        historyMode: 'none',
        scroll: false,
      })
    })
    void this.load()
  }

  disconnectedCallback() {
    this.listenerScope?.abort()
    this.requestGate?.abort()
    this.listenerScope = undefined
    this.requestGate = undefined
  }

  async load() {
    if (!this.listElement || !this.paginationElement || !this.requestGate) return
    const request = this.requestGate.begin()
    this.listElement.setAttribute('aria-busy', 'true')

    try {
      const result = await loadCurrentBlogArticles(this.dataset.blogHandle || '', {
        signal: request.signal,
      })
      if (!request.isCurrent()) return
      this.articles = result.articles
      this.totalPages = Math.max(1, Math.ceil(this.articles.length / PAGE_SIZE))
      this.currentPage = pageFromUrl(globalThis.window.location.href, this.totalPages)
      if (this.statusElement) {
        this.statusElement.textContent = statusForResult(
          result,
          this.dataset.emptyLabel,
          this.dataset.errorLabel,
          this.dataset.partialLabel,
        )
      }
      this.showPage(this.currentPage, { historyMode: 'none', scroll: false })
    } catch (error) {
      if (error?.name !== 'AbortError' && request.isCurrent() && this.statusElement) {
        this.statusElement.textContent = this.dataset.errorLabel
      }
    } finally {
      if (request.isCurrent()) this.listElement.removeAttribute('aria-busy')
    }
  }

  showPage(page, { historyMode = 'push', scroll = true } = {}) {
    if (!this.listElement || !this.paginationElement || !this.articles) return
    const focusKey = this.paginationFocusKey()
    this.currentPage = normalizePage(page, this.totalPages)
    this.listElement.replaceChildren(
      ...getPageItems(this.articles, this.currentPage).map((article) =>
        articleCard(article, this.placeholderTemplate),
      ),
    )
    this.renderPagination()
    this.restorePaginationFocus(focusKey)

    const nextUrl = urlForPage(globalThis.window.location.href, this.currentPage)
    if (historyMode === 'push') {
      globalThis.window.history.pushState({ page: this.currentPage }, '', nextUrl)
    }
    if (historyMode === 'replace') {
      globalThis.window.history.replaceState({ page: this.currentPage }, '', nextUrl)
    }
    if (scroll) this.scrollIntoView({ behavior: scrollBehavior(), block: 'start' })
  }

  pageButton(label, page, { current = false, disabled = false, className = '' } = {}) {
    const button = element('button', `blog-default-latest__page ${className}`.trim(), label)
    button.type = 'button'
    button.disabled = disabled
    button.dataset.blogPage = String(page)
    if (current) button.setAttribute('aria-current', 'page')
    return button
  }

  paginationFocusKey() {
    const activeElement = globalThis.document.activeElement
    if (!activeElement || !this.paginationElement?.contains(activeElement)) return ''
    if (activeElement.dataset?.blogPage) return `page:${activeElement.dataset.blogPage}`
    return activeElement.dataset?.blogFocus || ''
  }

  restorePaginationFocus(focusKey) {
    if (!focusKey || !this.paginationElement) return
    const selector = focusKey.startsWith('page:')
      ? `[data-blog-page="${focusKey.slice(5)}"]`
      : `[data-blog-focus="${focusKey}"]`
    this.paginationElement.querySelector(selector)?.focus()
  }

  renderPagination() {
    const pagination = this.paginationElement
    pagination.replaceChildren()
    if (this.articles.length <= PAGE_SIZE) {
      pagination.hidden = true
      return
    }
    pagination.hidden = false

    const isJapanese = (this.dataset.locale || '').toLowerCase().startsWith('ja')
    const totalText = isJapanese
      ? `全${this.totalPages}件`
      : `${this.dataset.totalLabel || 'Total'} ${this.totalPages}`
    pagination.append(element('span', 'blog-default-latest__total', totalText))
    pagination.append(
      this.pageButton(this.dataset.previousLabel || 'Previous', this.currentPage - 1, {
        disabled: this.currentPage === 1,
        className: 'blog-default-latest__arrow blog-default-latest__arrow--previous',
      }),
    )

    const paginationItems = paginationWindow(this.currentPage, this.totalPages)
    for (const [index, item] of paginationItems.entries()) {
      if (String(item).startsWith('ellipsis')) {
        const nextPage = paginationGapTarget(paginationItems, index, this.currentPage)
        const gapButton = this.pageButton('…', nextPage, {
          className: `blog-default-latest__ellipsis ${item}`,
        })
        if (this.dataset.pageLabel) {
          gapButton.setAttribute('aria-label', this.dataset.pageLabel.replace('__page__', nextPage))
        }
        pagination.append(gapButton)
      } else {
        pagination.append(
          this.pageButton(String(item), item, { current: item === this.currentPage }),
        )
      }
    }

    pagination.append(
      this.pageButton(this.dataset.nextLabel || 'Next', this.currentPage + 1, {
        disabled: this.currentPage === this.totalPages,
        className: 'blog-default-latest__arrow blog-default-latest__arrow--next',
      }),
    )

    const form = element('form', 'blog-default-latest__goto')
    form.dataset.blogGoto = ''
    const id = `blog-page-${this.dataset.sectionId || 'latest'}`
    const label = element('label', '', this.dataset.gotoLabel || 'Go to Page')
    label.htmlFor = id
    const input = element('input', 'blog-default-latest__input')
    input.id = id
    input.type = 'number'
    input.min = '1'
    input.max = String(this.totalPages)
    input.inputMode = 'numeric'
    input.dataset.blogFocus = 'goto'
    if (isJapanese) form.append(input, label)
    else form.append(label, input)
    pagination.append(form)
  }
}

if (globalThis.customElements && !globalThis.customElements.get('blog-default-navigation')) {
  globalThis.customElements.define('blog-default-navigation', BlogDefaultNavigation)
}

if (globalThis.customElements && !globalThis.customElements.get('blog-default-latest')) {
  globalThis.customElements.define('blog-default-latest', BlogDefaultLatest)
}
